import React, { useState } from "react";
import styled from "styled-components";
import { CheckIon } from "../assets/Icons";
import useTranslation from "../../hooks/useTranslation";
import {
  bodyTypeOptions,
  BuildInCameraOptions,
  lightingTypeOptions,
  nettierToolKitOptions,
} from "./options-data";

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: ${({ theme }) => theme.textSecondary}; 
  gap: 1rem;
  grid-column: 1 / 4;
`;

const CarFeatures = ({ carData }) => {
  const [activeTab, setActiveTab] = useState("specifications");
  const { t, language } = useTranslation();

  const handleMatch = (value, options) => {
    if (!value) return "-";
    const option = options.find((option) => option.value === value);
    return option ? option[`title${language === "en" ? "" : "_ar"}`] : value;
  };

  const tabs = [
    { id: "specifications", label: t.comparison.specifications },
    { id: "safety", label: t.comparison.safety },
    { id: "luxury", label: t.comparison.luxury },
    { id: "convenience", label: t.comparison.convenienceComfort },
    { id: "entertainment", label: t.comparison.entertainmentTechnology },
  ];

  const specifications = [
    { label: t.comparison.wheelSize, value: carData?.wheel_size || "-" },
    {
      label: t.comparison.bodyType,
      value: handleMatch(carData?.body_type, bodyTypeOptions),
    },
    { label: t.comparison.vinNumber, value: carData?.vin_number || "-" },
    {
      label: t.comparison.builtInCamera,
      value: handleMatch(carData?.built_in_camera, BuildInCameraOptions),
    },
    {
      label: t.comparison.bodyVolume,
      value: `${carData?.num_of_seats || 0} ${t.car.seats}`,
    },
    {
      label: t.comparison.interiorToolKit,
      value: handleMatch(carData?.interior_tool_kit, nettierToolKitOptions),
    },
    {
      label: t.comparison.lightingType,
      value: handleMatch(carData?.lighting_type, lightingTypeOptions),
    },
    {
      label: t.comparison.maxTorque,
      value: `${carData?.max_torque || 0} ${t.car["lb-ft"]}`,
    },
    {
      label: t.comparison.warrantyService,
      value: `${carData?.warranty_km || 0} ${t.car.km}`,
    },
  ];

  const safetyFeatures = [
    { label: t.comparison.airbags, value: carData?.airbag },
    {
      label: t.comparison.blindSpotSensor,
      value: carData?.has_blind_spot_sensor,
    },
    { label: t.comparison.sideAirbags, value: carData?.has_side_airbags },
    {
      label: t.comparison.stabilityControl,
      value: carData?.has_stability_control,
    },
    {
      label: t.comparison.driverPassengerAirbags,
      value: carData?.has_driver_passenger_airbags,
    },
    {
      label: t.comparison.frontRearSensors,
      value: carData?.has_front_rear_sensors,
    },
    { label: t.comparison.alarmSystem, value: carData?.has_alarm_system },
    { label: t.comparison.brakeAssist, value: carData?.has_brake_assist },
    {
      label: t.comparison.laneDepartureWarning,
      value: carData?.has_lane_departure_warning,
    },
    { label: t.comparison.rearAirbags, value: carData?.has_rear_airbags },
    {
      label: t.comparison.electronicBrakeForceDistribution,
      value: carData?.has_electronic_brake_force_distribution,
    },
    {
      label: t.comparison.tirePressureMonitor,
      value: carData?.has_tire_pressure_monitor,
    },
    { label: t.comparison.drivingModes, value: carData?.has_driving_modes },
    { label: t.comparison.fogLights, value: carData?.has_fog_lights },
    {
      label: t.comparison.protectionVisors,
      value: carData?.has_protection_visors,
    },
    { label: t.comparison.camera360, value: carData?.has_360_camera },
    {
      label: t.comparison.seatBeltReminder,
      value: carData?.has_seat_belt_reminder,
    },
    { label: t.comparison.abs, value: carData?.has_abs },
    {
      label: t.comparison.sunProtectionVisors,
      value: carData?.has_sun_protection_visors,
    },
    {
      label: t.comparison.tractionControl,
      value: carData?.has_traction_control,
    },
    {
      label: t.comparison.daytimeRunningLights,
      value: carData?.has_daytime_running_lights,
    },
    {
      label: t.comparison.collisionAvoidance,
      value: carData?.has_collision_avoidance,
    },
    { label: t.comparison.interiorLights, value: carData?.has_led_interior_lights },
    { label: t.comparison.isofix, value: carData?.has_isofix },
    { label: t.comparison.hillAssistSystem, value: carData?.has_hill_assist_system },

  ];

  const luxuryFeatures = [
    { label: t.comparison.panoramaRoof, value: carData?.roof === "panorama" },
    { label: t.comparison.cooledSeats, value: carData?.has_cooled_seats },
    {
      label: t.comparison.integratedChrome,
      value: carData?.has_integrated_chrome,
    },
    { label: t.comparison.headUpDisplay, value: carData?.has_head_up_display },
    {
      label: t.comparison.activeMotionFrontSeats,
      value: carData?.has_active_motion_front_seats,
    },
    { label: t.comparison.leatherSeats, value: carData?.has_leather_seats },
    {
      label: t.comparison.wirelessChargingPad,
      value: carData?.has_wireless_charging_pad,
    },
    { label: t.comparison.premiumWheels, value: carData?.has_premium_wheels },
    {
      label: t.comparison.doorCourtesyLamp,
      value: carData?.has_door_courtesy_lamp_with_logo,
    },
    {
      label: t.comparison.interiorAmbientLighting,
      value: carData?.has_interior_ambient_lighting,
    },
    { label: t.comparison.display, value: carData?.has_display },
    {
      label: t.comparison.powerSeatsLumbarControl,
      value: carData?.has_power_seats_lumbar_control,
    },
    { label: t.comparison.sunroof, value: carData?.has_sunroof },
    { label: t.comparison.panorama, value: carData?.has_panorama },
    { label: t.comparison.powerTrunk, value: carData?.has_power_trunk },
    { label: t.comparison.parkingAssist, value: carData?.has_parking_assist },
    { label: t.comparison.heatedSeats, value: carData?.has_heated_seats },
  ];

  const entertainmentFeatures = [
    { label: t.comparison.navigation, value: carData?.has_navigation },
    {
      label: t.comparison.premiumSoundSystem,
      value: carData?.has_premium_sound_system,
    },
    {
      label: `${carData?.speakers || 0} ${t.comparison.speakers}`,
      value: true,
    },
    { label: t.comparison.bluetooth, value: carData?.has_bluetooth },
    { label: t.comparison.securitySystem, value: carData?.has_security_system },
    { label: t.comparison.voiceCommand, value: carData?.has_voice_command },
    { label: t.comparison.radio, value: carData?.has_radio },
    { label: t.comparison.appleCarplay, value: carData?.has_apple_carplay },
    { label: t.comparison.usbConnection, value: carData?.has_usb_connection },
    {
      label: t.comparison.driveModeSelect,
      value: carData?.has_drive_mode_select,
    },
    { label: t.comparison.remoteStart, value: carData?.has_Remote_start },
    { label: t.comparison.secuirtySystem, value: carData?.has_secuirty_system },
    { label: t.comparison.aux, value: carData?.has_aux },
    { label: t.comparison.hardDrive, value: carData?.has_hard_drive },
    { label: t.comparison.handsFree, value: carData?.has_hands_free },
    { label: t.comparison.androidAuto, value: carData?.android_auto },
    { label: t.comparison.cd, value: carData?.has_cd },

  ];

  const convenienceFeatures = [
    { label: t.comparison.cruiseControl, value: carData?.has_cruise_control },
    { label: t.comparison.keylessEntry, value: carData?.has_keyless_entry },
    {
      label: t.comparison.startEngineButton,
      value: carData?.has_start_engine_button,
    },
    { label: t.comparison.rearCamera, value: carData?.has_rear_camera },
    { label: t.comparison.memorySeating, value: carData?.has_memory_seating },
    { label: t.comparison.powerLocks, value: carData?.has_power_locks },
    { label: t.comparison.powerWindows, value: carData?.has_power_windows },
    {
      label: t.comparison.steeringWheelControls,
      value: carData?.has_steering_wheel_controls,
    },
    { label: t.comparison.remoteStart, value: carData?.has_remote_start },
    {
      label: t.comparison.powerSideMirrors,
      value: carData?.has_power_side_mirrors,
    },
    {
      label: t.comparison.intelligentCruiseControl,
      value: carData?.has_intelligent_cruise_control,
    },
    {
      label: t.comparison.leatherSteeringWheel,
      value: carData?.has_leather_steering_wheel,
    },
    {
      label: t.comparison.autoDimmingRearView,
      value: carData?.has_auto_dimming_rear_view,
    },
    { label: t.comparison.floorMats, value: carData?.has_floor_mats },
    {
      label: t.comparison.multiZoneClimateControl,
      value: carData?.has_multi_zone_climate_control,
    },
    { label: t.comparison.manualAC, value: carData?.has_manual_ac },
    { label: t.comparison.automaticAC, value: carData?.has_automatic_ac },
    {
      label: t.comparison.instrumentCluster,
      value: carData?.has_instrument_cluster,
    },
  ];

  const renderFeatures = () => {
    const renderEmptyState = () => (
      <EmptyState>
        <span>{t.noFeaturesAvailable}</span>
      </EmptyState>
    );

    const getFeaturesList = () => {
      switch (activeTab) {
        case "specifications":
          return specifications?.length > 0
            ? specifications.map((spec) => (
                <SpecificationRow key={spec.label}>
                  <CheckIon />
                  <SpecLabel>{spec.label}</SpecLabel>
                  <SpecValue>{spec.value || t("notSpecified")}</SpecValue>
                </SpecificationRow>
              ))
            : renderEmptyState();

        case "safety":
          const safetyItems = safetyFeatures.filter((f) => f.value);
          return safetyItems.length > 0
            ? safetyItems.map((feature) => (
                <FeatureItem key={feature.label}>
                  <StyledCheck />
                  <span>{feature.label}</span>
                </FeatureItem>
              ))
            : renderEmptyState();

        case "luxury":
          const luxuryItems = luxuryFeatures.filter((f) => f.value);
          return luxuryItems.length > 0
            ? luxuryItems.map((feature) => (
                <FeatureItem key={feature.label}>
                  <StyledCheck />
                  <span>{feature.label}</span>
                </FeatureItem>
              ))
            : renderEmptyState();

        case "convenience":
          const convenienceItems = convenienceFeatures.filter((f) => f.value);
          return convenienceItems.length > 0
            ? convenienceItems.map((feature) => (
                <FeatureItem key={feature.label}>
                  <StyledCheck />
                  <span>{feature.label}</span>
                </FeatureItem>
              ))
            : renderEmptyState();

        case "entertainment":
          const entertainmentItems = entertainmentFeatures.filter(
            (f) => f.value
          );
          return entertainmentItems.length > 0
            ? entertainmentItems.map((feature) => (
                <FeatureItem key={feature.label}>
                  <StyledCheck />
                  <span>{feature.label}</span>
                </FeatureItem>
              ))
            : renderEmptyState();

        default:
          return renderEmptyState();
      }
    };

    return <ContentSection>{getFeaturesList()}</ContentSection>;
  };

  return (
    <Container>
      <ContentWrapper>
        <Title>{t.car.features}</Title>

        <div className="tabWrapper">
          <TabsContainer>
            {tabs.map((tab) => (
              <TabButton
                key={tab.id}
                isActive={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </TabButton>
            ))}
          </TabsContainer>
        </div>

        {renderFeatures()}
      </ContentWrapper>
    </Container>
  );
};

export default CarFeatures;

// Styled Components
const Container = styled.div`
  width: calc(1248 / 1440 * 100vw);
  margin: 0 auto;
  background: white;
  border-radius: calc(8 / 1440 * 100vw);
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  * {
    font-family: ${(props) => (props.language === "en" ? "Inter" : "Almarai")};
  }
`;

const ContentWrapper = styled.div`
  margin-top: calc(32 / 1440 * 100vw);
  padding: calc(24 / 1440 * 100vw);

  .tabWrapper {
    overflow-x: auto;
  }

  @media (max-width: 768px) {
    margin-top: calc(16 / 375 * 100vw);
    padding: calc(16 / 375 * 100vw);
  }
`;

const Title = styled.h2`
  font-size: calc(24 / 1440 * 100vw);
  font-weight: bold;
  margin-bottom: calc(24 / 1440 * 100vw);

  @media (max-width: 768px) {
    font-size: calc(20 / 375 * 100vw);
    margin-bottom: calc(16 / 375 * 100vw);
  }
`;

const TabsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 768px) {
    width: max-content;
    overflow: scroll;
    border-bottom: none;
    padding-bottom: calc(8 / 375 * 100vw);
  }
`;

const TabButton = styled.button`
  padding: calc(8 / 1440 * 100vw) calc(16 / 1440 * 100vw);
  margin-bottom: -1px;
  color: ${(props) => (props.isActive ? "#2563eb" : "#6b7280")};
  border-bottom: ${(props) => (props.isActive ? "2px solid #2563eb" : "none")};
  font-weight: 800;
  transition: all 0.2s;
  background-color: transparent;
  font-size: calc(16 / 1440 * 100vw);

  &:hover {
    color: ${(props) => (props.isActive ? "#2563eb" : "#374151")};
  }

  @media (max-width: 768px) {
    font-size: calc(16 / 375 * 100vw);
    padding: calc(8 / 375 * 100vw) calc(16 / 375 * 100vw);
    width: fit-content;
  }
`;

const ContentSection = styled.div`
  margin-top: calc(24 / 1440 * 100vw);
  gap: calc(8 / 1440 * 100vw);
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, auto);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    margin-top: calc(16 / 375 * 100vw);
    gap: calc(8 / 375 * 100vw);
  }
`;

const SpecificationRow = styled.div`
  display: flex;
  align-items: center;
  gap: calc(8 / 1440 * 100vw);
  padding: calc(8 / 1440 * 100vw) 0;
  font-size: calc(16 / 1440 * 100vw);

  @media (max-width: 768px) {
    font-size: calc(16 / 375 * 100vw);
    gap: calc(8 / 375 * 100vw);
    padding: calc(8 / 375 * 100vw) 0;
  }
`;

const SpecLabel = styled.span`
  color: #4b5563;
`;

const SpecValue = styled.span`
  font-weight: 500;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: calc(8 / 1440 * 100vw);
  padding: calc(8 / 1440 * 100vw) 0;
  font-size: calc(16 / 1440 * 100vw);

  @media (max-width: 768px) {
    font-size: calc(16 / 375 * 100vw);
    gap: calc(8 / 375 * 100vw);
    padding: calc(8 / 375 * 100vw) 0;
  }
`;

const StyledCheck = styled(CheckIon)`
  width: calc(20 / 1440 * 100vw);
  height: calc(20 / 1440 * 100vw);
  color: #2563eb;

  @media (max-width: 768px) {
    width: calc(20 / 375 * 100vw);
    height: calc(20 / 375 * 100vw);
  }
`;
